import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as TypeIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../../services/api';

interface TypeItem {
  id: string;
  codigo: string;
  nome: string;
  descricao?: string;
  estado: string;
  lojas?: number; // For store types
  ui?: string; // For service types
}

const Types: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryTab, setCategoryTab] = useState<string>('client');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Data state for each type
  const [clientTypes, setClientTypes] = useState<TypeItem[]>([]);
  const [documentTypes, setDocumentTypes] = useState<TypeItem[]>([]);
  const [serviceTypes, setServiceTypes] = useState<TypeItem[]>([]);
  const [entityTypes, setEntityTypes] = useState<TypeItem[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<TypeItem[]>([]);
  const [storeTypes, setStoreTypes] = useState<TypeItem[]>([]);

  // Loading states
  const [loading, setLoading] = useState(false);

  // Edit dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<TypeItem | null>(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    estado: 'A',
    ui: 'N', // For service types
  });
  const [saving, setSaving] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message: string, severity: 'success' | 'error') => {
    setToast({ open: true, message, severity });
  };

  // API endpoint mapping
  const getApiEndpoint = (tab: string) => {
    switch (tab) {
      case 'client': return '/admin/settings/types/client';
      case 'document': return '/admin/settings/types/document';
      case 'service': return '/admin/services/types';
      case 'entity': return '/admin/settings/types/entity';
      case 'payment': return '/admin/settings/types/payment';
      case 'store': return '/admin/settings/types/store';
      default: return '';
    }
  };

  // Fetch types from API
  const fetchTypes = useCallback(async (tab: string) => {
    setLoading(true);
    try {
      const endpoint = getApiEndpoint(tab);
      const response = await api.get(`${endpoint}?all=true`);
      if (response.data.success) {
        const types = response.data.data || [];
        switch (tab) {
          case 'client': setClientTypes(types); break;
          case 'document': setDocumentTypes(types); break;
          case 'service': setServiceTypes(types); break;
          case 'entity': setEntityTypes(types); break;
          case 'payment': setPaymentTypes(types); break;
          case 'store': setStoreTypes(types); break;
        }
      }
    } catch (error) {
      console.error(`Error fetching ${tab} types:`, error);
      showToast(`Erro ao carregar tipos de ${tab}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes(categoryTab);
  }, [categoryTab, fetchTypes]);

  // Get current types based on tab
  const getCurrentTypes = (): TypeItem[] => {
    switch (categoryTab) {
      case 'client': return clientTypes;
      case 'document': return documentTypes;
      case 'service': return serviceTypes;
      case 'entity': return entityTypes;
      case 'payment': return paymentTypes;
      case 'store': return storeTypes;
      default: return [];
    }
  };

  // Filter types
  const filteredTypes = getCurrentTypes().filter(type =>
    searchTerm === '' ||
    type.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    type.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (type.descricao && type.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Stats
  const stats = {
    total: filteredTypes.length,
    active: filteredTypes.filter(t => t.estado === 'A').length,
  };

  const getStatusChip = (estado: string) => {
    const isActive = estado === 'A' || estado === 'S' || estado === 'E';
    return isActive ? (
      <Chip size="small" color="success" label="Activo" icon={<ActiveIcon />} />
    ) : (
      <Chip size="small" color="default" label="Inactivo" icon={<InactiveIcon />} />
    );
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (type?: TypeItem) => {
    if (type) {
      setSelectedType(type);
      setFormData({
        codigo: type.codigo || '',
        nome: type.nome || '',
        descricao: type.descricao || '',
        estado: type.estado || 'A',
        ui: type.ui || 'N',
      });
    } else {
      setSelectedType(null);
      setFormData({
        codigo: '',
        nome: '',
        descricao: '',
        estado: 'A',
        ui: 'N',
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedType(null);
  };

  const handleSave = async () => {
    if (!formData.nome || (categoryTab !== 'store' && !formData.codigo)) {
      showToast('Preencha os campos obrigatórios', 'error');
      return;
    }

    setSaving(true);
    try {
      const endpoint = getApiEndpoint(categoryTab);
      const url = selectedType ? `${endpoint}/${selectedType.id}` : endpoint;

      // Prepare body based on type
      let body: any = { nome: formData.nome, estado: formData.estado };
      if (categoryTab !== 'store') {
        body.codigo = formData.codigo;
        body.descricao = formData.descricao;
      }
      if (categoryTab === 'service') {
        body.desig = formData.nome;
        body.descr = formData.descricao;
        body.ui = formData.ui;
      }

      const response = selectedType
        ? await api.put(url, body)
        : await api.post(url, body);

      if (response.data.success) {
        showToast(
          selectedType ? 'Tipo atualizado com sucesso' : 'Tipo criado com sucesso',
          'success'
        );
        handleCloseEdit();
        fetchTypes(categoryTab);
      } else {
        showToast(response.data.error || 'Erro ao guardar tipo', 'error');
      }
    } catch (error: any) {
      console.error('Error saving type:', error);
      showToast(error.message || 'Erro ao guardar tipo', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type: TypeItem) => {
    if (!window.confirm(`Tem certeza que deseja desactivar o tipo "${type.nome}"?`)) {
      return;
    }

    try {
      const endpoint = getApiEndpoint(categoryTab);
      const response = await api.delete(`${endpoint}/${type.id}`);

      if (response.data.success) {
        showToast('Tipo desactivado com sucesso', 'success');
        fetchTypes(categoryTab);
      } else {
        showToast(response.data.error || 'Erro ao desactivar tipo', 'error');
      }
    } catch (error: any) {
      console.error('Error deleting type:', error);
      showToast(error.message || 'Erro ao desactivar tipo', 'error');
    }
  };

  // Get tab title
  const getTabTitle = () => {
    switch (categoryTab) {
      case 'client': return 'Cliente';
      case 'document': return 'Documento';
      case 'service': return 'Serviço';
      case 'entity': return 'Entidade';
      case 'payment': return 'Pagamento';
      case 'store': return 'Loja';
      default: return '';
    }
  };

  // Check if type has extra column (lojas for store, ui for service)
  const hasExtraColumn = categoryTab === 'store' || categoryTab === 'service';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Tipos do Sistema
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure os diversos tipos utilizados na plataforma
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TypeIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Tipos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.active}</Typography>
                  <Typography variant="caption" color="text.secondary">Activos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Category Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={categoryTab}
          onChange={(_, newValue) => {
            setCategoryTab(newValue);
            setPage(0);
            setSearchTerm('');
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Cliente" value="client" />
          <Tab label="Documento" value="document" />
          <Tab label="Serviço" value="service" />
          <Tab label="Entidade" value="entity" />
          <Tab label="Pagamento" value="payment" />
          <Tab label="Lojas" value="store" />
        </Tabs>
      </Card>

      {/* Filters and Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Pesquisar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchTypes(categoryTab)}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
            Novo Tipo de {getTabTitle()}
          </Button>
        </Box>
      </Box>

      {/* Types Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {categoryTab !== 'store' && <TableCell width={100}>Código</TableCell>}
                    <TableCell>Nome</TableCell>
                    {categoryTab !== 'store' && <TableCell>Descrição</TableCell>}
                    {categoryTab === 'service' && (
                      <TableCell width={100} align="center">Interface</TableCell>
                    )}
                    {categoryTab === 'store' && (
                      <TableCell width={100} align="center">Lojas</TableCell>
                    )}
                    <TableCell width={100}>Estado</TableCell>
                    <TableCell width={120} align="center">Acções</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={hasExtraColumn ? 5 : 4} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          Nenhum tipo encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTypes
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((type) => (
                        <TableRow key={type.id} hover>
                          {categoryTab !== 'store' && (
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                                {type.codigo}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {type.nome}
                            </Typography>
                          </TableCell>
                          {categoryTab !== 'store' && (
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{
                                maxWidth: 350,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {type.descricao || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {categoryTab === 'service' && (
                            <TableCell align="center">
                              <Tooltip title={type.ui === 'S' ? 'Tem interface' : 'Sem interface'}>
                                <Chip
                                  size="small"
                                  label={type.ui === 'S' ? 'Sim' : 'Não'}
                                  color={type.ui === 'S' ? 'success' : 'default'}
                                  icon={type.ui === 'S' ? <ActiveIcon /> : <InactiveIcon />}
                                />
                              </Tooltip>
                            </TableCell>
                          )}
                          {categoryTab === 'store' && (
                            <TableCell align="center">
                              <Chip size="small" label={type.lojas || 0} color="primary" />
                            </TableCell>
                          )}
                          <TableCell>{getStatusChip(type.estado)}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleOpenEdit(type)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(type)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredTypes.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypeIcon />
          {selectedType ? `Editar Tipo de ${getTabTitle()}` : `Novo Tipo de ${getTabTitle()}`}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {categoryTab !== 'store' && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Código"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  inputProps={{ maxLength: 5 }}
                  required
                  disabled={!!selectedType}
                  helperText={selectedType ? 'Código não pode ser alterado' : 'Max 5 caracteres'}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: categoryTab === 'store' ? 12 : 8 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                inputProps={{ maxLength: 75 }}
                required
              />
            </Grid>
            {categoryTab !== 'store' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Descrição"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  inputProps={{ maxLength: 150 }}
                  multiline
                  rows={2}
                />
              </Grid>
            )}
            {categoryTab === 'service' && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Interface de Utilizador"
                  value={formData.ui}
                  onChange={(e) => setFormData({ ...formData, ui: e.target.value })}
                  helperText="Permite acesso à interface de criação"
                >
                  <MenuItem value="S">Sim</MenuItem>
                  <MenuItem value="N">Não</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: categoryTab === 'service' ? 6 : 12 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">Activo</MenuItem>
                <MenuItem value="I">Inactivo</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.nome || (categoryTab !== 'store' && !formData.codigo)}
            startIcon={saving ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {saving ? 'A guardar...' : selectedType ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Types;
